import { useCallback, useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'slai-theme';

type ThemeListener = () => void;

const listeners = new Set<ThemeListener>();

let currentTheme: Theme = 'light';

function getSystemTheme(): Theme {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getStoredTheme(): Theme {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
        return stored;
    }

    return getSystemTheme();
}

export function applyTheme(theme: Theme): void {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    root.classList.toggle('dark', theme === 'dark');
}

function setThemeState(theme: Theme): void {
    currentTheme = theme;
    localStorage.setItem(STORAGE_KEY, theme);
    applyTheme(theme);
    listeners.forEach((listener) => listener());
}

function subscribe(listener: ThemeListener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

function getSnapshot(): Theme {
    return currentTheme;
}

function getServerSnapshot(): Theme {
    return 'light';
}

export function initializeTheme(): Theme {
    currentTheme = getStoredTheme();
    applyTheme(currentTheme);
    return currentTheme;
}

export interface UseThemeResult {
    theme: Theme;
    resolvedTheme: Theme;
    setTheme: (theme: Theme) => void;
}

export function useTheme(): UseThemeResult {
    const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

    const setTheme = useCallback((nextTheme: Theme) => {
        setThemeState(nextTheme);
    }, []);

    return {
        theme,
        resolvedTheme: theme,
        setTheme,
    };
}
